// @core/stores/workflow/WorkflowPipelinePolicyStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { workflowApi } from '@core/api';
import type {
  CreatePipelinePolicyRequest,
  PipelinePolicy,
  ResolvePipelineContextRequest,
  ResolvePipelineContextResponse,
  UpdatePipelinePolicyRequest,
} from '@core/types/workflow';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class WorkflowPipelinePolicyStore {
  // List state
  items: PipelinePolicy[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: PipelinePolicy | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, PipelinePolicy>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): PipelinePolicy | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: PipelinePolicy | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create pipeline policy
   */
  async CreatePipelinePolicy(data: CreatePipelinePolicyRequest): Promise<PipelinePolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await workflowApi.CreatePipelinePolicy(data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete pipeline policy
   */
  async DeletePipelinePolicy(policy_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await workflowApi.DeletePipelinePolicy(policy_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== policy_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === policy_id) {
          this.currentItem = null;
        }

        this.removeFromCache(policy_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get pipeline policy
   */
  async GetPipelinePolicy(policy_id: string): Promise<PipelinePolicy> {
    // Check cache first
    const cached = this.cache.get(policy_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await workflowApi.GetPipelinePolicy(policy_id);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get entity pipeline policy
   */
  async GetPipelinePolicyByEntity(entity_type: string, entity_id: string): Promise<PipelinePolicy> {
    // Check cache first
    const cached = this.cache.get(entity_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await workflowApi.GetPipelinePolicyByEntity(entity_type, entity_id);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List pipeline policies
   */
  async ListPipelinePolicies(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await workflowApi.ListPipelinePolicies(params);

      runInAction(() => {
        this.items = response.policies?.policies ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve pipeline context
   */
  async ResolvePipelineContext(data: ResolvePipelineContextRequest): Promise<ResolvePipelineContextResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ResolvePipelineContext(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update pipeline policy
   */
  async UpdatePipelinePolicy(policy_id: string, data: UpdatePipelinePolicyRequest): Promise<PipelinePolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await workflowApi.UpdatePipelinePolicy(policy_id, data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const workflowPipelinePolicyStore = new WorkflowPipelinePolicyStore();
